import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { useClientSession } from '@/lib/ClientSessionContext';
import { ShieldAlert, CalendarClock, Settings2, Sparkles, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getPageAccess } from '@/lib/AuthSession';

export default function PortalGuard({ children }) {
  const { member } = useSession();
  const { clientUser } = useClientSession();
  const location = useLocation();
  const path = location.pathname;

  const { data: settingsList = [], isLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#F9F5EC]">
        <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-[#302621] rounded-full animate-spin"></div>
      </div>
    );
  }

  const settings = settingsList[0] || {};
  const status = settings.portal_status || {};
  
  // Identify which portal we are in
  let currentPortal = null;
  let activeStatus = 'normal';
  
  if (path === '/login' || path === '/login-tim') {
    currentPortal = null;
  } else if (path === '/' || path.startsWith('/pesan-po') || path.startsWith('/daftar-pesanan') || path.startsWith('/pesanan/') || path.startsWith('/database-produk') || path.startsWith('/artwork') || path.startsWith('/profil-saya')) {
    currentPortal = 'client';
    activeStatus = status.landing || 'normal';
  } else if (path.startsWith('/office/production')) {
    currentPortal = 'production';
    activeStatus = status.production || 'normal';
  } else if (path.startsWith('/office/finance')) {
    currentPortal = 'finance';
    activeStatus = status.finance || 'normal';
  } else if (path.startsWith('/office/workspace')) {
    currentPortal = 'workspace';
    activeStatus = status.workspace || 'normal';
  } else if (path.startsWith('/office/marketing')) {
    currentPortal = 'marketing';
    activeStatus = status.marketing || 'normal';
  } else if (path.startsWith('/office/purchasing')) {
    currentPortal = 'purchasing';
    activeStatus = status.purchasing || 'normal';
  } else if (path.startsWith('/office/analytics')) {
    currentPortal = 'analytics';
    activeStatus = status.analytics || 'normal';
  }

  // ── RBAC Portal Access Guard ──────────────────────────────────
  // If member's portal access is set to 'none', redirect to hub
  const portalAccessKeyMap = {
    production: 'portal_production',
    finance: 'portal_finance',
    workspace: 'portal_workspace',
    marketing: 'portal_marketing',
    purchasing: 'portal_purchasing',
    analytics: 'portal_analytics',
  };

  if (member && currentPortal && portalAccessKeyMap[currentPortal]) {
    const portalAccessKey = portalAccessKeyMap[currentPortal];
    const effectiveAccess = getPageAccess(member, portalAccessKey);
    
    if (effectiveAccess === 'none') {
      // Developer and Owner always bypass the portal block
      if (member.role === 'developer' || member.role === 'owner') {
        // let them pass
      }
      // Exception: Finance role inherently has access to analytics unless explicitly revoked
      else if (currentPortal === 'analytics' && member.role === 'finance') {
        // let them pass
      } else {
        return <Navigate to="/office" replace />;
      }
    }
  }



  const isClientDev = clientUser && clientUser.email && clientUser.email.toLowerCase() === 'satalawebdeveloper@gmail.com';

  const isBypassed = (member && (member.role === 'developer' || member.role === 'owner')) || isClientDev;
  const isBlocked = activeStatus !== 'normal' && currentPortal !== null;

  if (isBlocked && !isBypassed) {
    const isHoliday = activeStatus === 'holiday';
    const Icon = isHoliday ? CalendarClock : Settings2;
    const title = isHoliday ? 'Pabrik Sedang Libur' : 'Sistem Dalam Perbaikan';
    const message = isHoliday 
      ? (status.custom_message || 'Seluruh kegiatan operasional sedang diliburkan. Kami akan segera kembali beroperasi.') 
      : (status.custom_message || 'Portal ini sedang dalam perbaikan rutin untuk meningkatkan kualitas sistem kami. Mohon kembali lagi nanti.');

    const getPortalDisplayName = (p) => {
      if (p === 'client') return 'Satala Dermatech Essential';
      if (p === 'production') return 'Portal Produksi';
      if (p === 'finance') return 'Portal Finance';
      if (p === 'workspace') return 'Portal Workspace';
      if (p === 'marketing') return 'Portal Marketing';
      if (p === 'analytics') return 'Portal Analytics Management';
      return '';
    };

    return (
      <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center p-4 md:p-6 relative overflow-hidden">
        {/* Decorative BG */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#E6D5B8] rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 right-0 w-80 h-80 bg-[#D4AF37]/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-[280px] md:max-w-lg w-full bg-white border border-[#E6D5B8] rounded-2xl md:rounded-3xl p-6 md:p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.06)] md:shadow-xl relative z-10 animate-in zoom-in-95 duration-500">
          <div className="w-12 h-12 md:w-24 md:h-24 bg-gradient-to-br from-[#F9F5EC] to-[#E6D5B8] rounded-full flex items-center justify-center mx-auto mb-5 md:mb-6 shadow-inner border border-[#D4AF37]/30">
            <Icon className="w-5 h-5 md:w-12 md:h-12 text-[#8C6208]" strokeWidth={1.5} />
          </div>
          
          <h1 className="text-[15px] md:text-3xl font-black text-[#302621] mb-2 md:mb-4 tracking-tight whitespace-nowrap md:whitespace-normal">{title}</h1>
          <p className="text-[#7A695B] text-[10px] md:text-base leading-relaxed mb-6 md:mb-8 font-medium md:font-normal">{message}</p>
          
          <div className="bg-[#F9F5EC] rounded-lg md:rounded-xl p-3 md:p-4 border border-[#E6D5B8]/50 mb-0 md:mb-8 text-left flex gap-2 md:gap-3">
            <ShieldAlert className="w-3.5 h-3.5 md:w-5 md:h-5 text-[#8C6208] shrink-0 mt-0.5" />
            <p className="text-[9px] md:text-xs text-[#4A3B32] leading-[1.5] md:leading-relaxed">
              Akses ke <strong>{getPortalDisplayName(currentPortal)}</strong> sedang ditutup sementara untuk mencegah kesalahan data selama masa operasional dihentikan.
            </p>
          </div>

          {currentPortal !== 'client' && (
            <Button 
              onClick={() => window.location.href = '/login-tim'} 
              className="w-full bg-[#302621] hover:bg-[#1A1411] text-white font-bold h-12 rounded-xl transition-all mt-4"
            >
              Login Owner / Developer
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {isBlocked && isBypassed && (
        <div className="bg-amber-100/90 text-amber-900 px-4 py-2 text-xs font-bold text-center border-b border-amber-200 sticky top-0 z-[100] flex justify-center items-center gap-2 ">
          <ShieldAlert className="w-4 h-4" />
          PERHATIAN: Portal ini sedang dalam mode {activeStatus.toUpperCase()}. User lain tidak dapat mengakses halaman ini. (Bypass Aktif)
        </div>
      )}
      {children}
    </>
  );
}
